/**
 * Service-day helper — mirrors bar_manual.current_service_date() in Postgres.
 *
 * The bar's service day runs from 6:00 AM (America/Chicago) to 6:00 AM the
 * next day. Anything before 6 AM local belongs to YESTERDAY's service day
 * (the bartender is still finishing yesterday's shift).
 *
 * Two surfaces use this:
 *   1. Edge function bar-list-drinks — to compute "today" + "30 days ago"
 *      when querying recipe_activity_daily.
 *   2. /dev/tests page — verifies cutover boundaries.
 *
 * Always returns YYYY-MM-DD format to match Postgres `date` columns.
 */

const SERVICE_TZ = "America/Chicago";
const SERVICE_DAY_START_HOUR = 6;

interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
}

function localPartsIn(now: Date, tz: string): LocalParts {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
  });
  const map: Record<string, string> = {};
  for (const p of fmt.formatToParts(now)) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour) % 24, // Intl can return "24" — normalize
  };
}

function pad(n: number): string {
  return n < 10 ? "0" + n : String(n);
}

function ymd(y: number, m: number, d: number): string {
  return `${y}-${pad(m)}-${pad(d)}`;
}

/**
 * Returns the service date (YYYY-MM-DD) corresponding to the given moment
 * in America/Chicago, with the 6:00 AM cutover.
 *
 * @example
 *   getServiceDate(new Date("2026-04-30T17:00:00Z")) // "2026-04-30" (12pm CDT)
 *   getServiceDate(new Date("2026-04-30T09:00:00Z")) // "2026-04-29" (4am CDT)
 *   getServiceDate(new Date("2026-04-30T11:00:00Z")) // "2026-04-30" (6am CDT — boundary)
 */
export function getServiceDate(now: Date = new Date()): string {
  const local = localPartsIn(now, SERVICE_TZ);
  if (local.hour >= SERVICE_DAY_START_HOUR) {
    return ymd(local.year, local.month, local.day);
  }
  // Before 6 AM — return YESTERDAY's local date.
  // Subtract a day from the LOCAL date by anchoring at noon UTC of that
  // local date and going back 24h (avoids DST off-by-one).
  const anchor = Date.UTC(local.year, local.month - 1, local.day, 12);
  const prev = new Date(anchor - 24 * 60 * 60 * 1000);
  return ymd(prev.getUTCFullYear(), prev.getUTCMonth() + 1, prev.getUTCDate());
}

/** Returns the date `daysAgo` service-days before the current service date. */
export function getServiceDateOffset(daysAgo: number, now: Date = new Date()): string {
  const today = getServiceDate(now);
  const [y, m, d] = today.split("-").map(Number);
  const anchor = Date.UTC(y, m - 1, d, 12);
  const offset = new Date(anchor - daysAgo * 24 * 60 * 60 * 1000);
  return ymd(offset.getUTCFullYear(), offset.getUTCMonth() + 1, offset.getUTCDate());
}
